import {
  AwaitContainer,
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableColumn,
  Tooltip,
} from '@ibsys/kit';
import { FC, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { C3AsrDemoLoadItem, useC3AsrDemoLoads, useStartC3AsrDemoLoad } from '@/entities/integrations';
import { toNumberOrUndefined } from '@/shared/lib/utils/number';
import { toastError, toastSuccess } from '@/shared/lib/utils/toast';

const PER_PAGE = 20;

// Форма интервала загрузки: границы id и ограничение количества строк
type C3LoadForm = {
  idFrom?: number;
  idTo?: number;
  limit?: number;
};

export const C3AsrDemoSection: FC = () => {
  const { t } = useTranslation(['integrations', 'common']);
  const [page, setPage] = useState(1);

  const { control, handleSubmit } = useForm<C3LoadForm>({
    defaultValues: { idFrom: undefined, idTo: undefined, limit: undefined },
  });

  const { mutateAsync: startLoad, isPending: isStarting } = useStartC3AsrDemoLoad();
  const { data, isLoading, isFetching } = useC3AsrDemoLoads({
    limit: PER_PAGE,
    offset: (page - 1) * PER_PAGE,
  });

  const handleLoad = useCallback(
    async (values: C3LoadForm) => {
      try {
        await startLoad({
          idFrom: toNumberOrUndefined(values.idFrom),
          idTo: toNumberOrUndefined(values.idTo),
          limit: toNumberOrUndefined(values.limit),
        });
        setPage(1);
        toastSuccess(t('integrations:c3AsrDemo.startSuccess'));
      } catch {
        toastError(t('integrations:c3AsrDemo.startError'));
      }
    },
    [startLoad, t],
  );

  const handleChangePagination = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  // Обе границы необязательны, но заданные вместе должны идти по возрастанию
  const validateIdRange = useCallback(
    (value: number | undefined, formValues: C3LoadForm) => {
      const idFrom = toNumberOrUndefined(formValues.idFrom);
      const idTo = toNumberOrUndefined(value);
      if (idFrom !== undefined && idTo !== undefined && idFrom > idTo) {
        return t('integrations:c3AsrDemo.errorRange');
      }
      return true;
    },
    [t],
  );

  const columns: TableColumn<C3AsrDemoLoadItem>[] = useMemo(
    () => [
      {
        accessorKey: 'sourceId',
        header: () => <span>{t('integrations:c3AsrDemo.table.sourceId')}</span>,
        size: 90,
        cell: ({ row }) => <span className="text-sm">{row.original.sourceId}</span>,
      },
      {
        accessorKey: 'callId',
        header: () => <span>{t('integrations:c3AsrDemo.table.callId')}</span>,
        size: 140,
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.callId}</span>,
      },
      {
        accessorKey: 'callType',
        header: () => <span>{t('integrations:c3AsrDemo.table.callType')}</span>,
        size: 110,
        cell: ({ row }) => <span className="text-sm">{row.original.callType ?? ''}</span>,
      },
      {
        accessorKey: 'conversationId',
        header: () => <span>{t('integrations:c3AsrDemo.table.conversationId')}</span>,
        size: 130,
        cell: ({ row }) => <span className="text-sm">{row.original.conversationId ?? ''}</span>,
      },
      {
        accessorKey: 'operatorFio',
        header: () => <span>{t('integrations:c3AsrDemo.table.operatorFio')}</span>,
        size: 200,
        cell: ({ row }) => <span className="text-sm">{row.original.operatorFio ?? ''}</span>,
      },
      {
        accessorKey: 'status',
        header: () => <span>{t('integrations:c3AsrDemo.table.status')}</span>,
        size: 120,
        cell: ({ row }) => {
          const isLoaded = row.original.status === 'loaded';
          const badge = (
            <Badge variant={isLoaded ? 'primary' : 'destructive'}>
              {isLoaded ? t('integrations:c3AsrDemo.table.loaded') : t('integrations:c3AsrDemo.table.error')}
            </Badge>
          );
          // Текст ошибки произвольной длины — показываем подсказкой, а не отдельной колонкой
          return row.original.error ? <Tooltip tooltip={row.original.error}>{badge}</Tooltip> : badge;
        },
      },
      {
        accessorKey: 'updatedAt',
        header: () => <span>{t('integrations:c3AsrDemo.table.updatedAt')}</span>,
        size: 180,
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.updatedAt ? new Date(row.original.updatedAt).toLocaleString() : ''}
          </span>
        ),
      },
    ],
    [t],
  );

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">{t('integrations:c3AsrDemo.title')}</CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit(handleLoad)}>
        <CardContent className="flex flex-col gap-3">
          <p className="text-muted-foreground text-sm">{t('integrations:c3AsrDemo.description')}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              control={control}
              name="idFrom"
              type="number"
              label={t('integrations:c3AsrDemo.filter.idFrom')}
              placeholder={t('integrations:c3AsrDemo.filter.idFromPlaceholder')}
              rules={{ deps: ['idTo'] }}
            />
            <Input
              control={control}
              name="idTo"
              type="number"
              label={t('integrations:c3AsrDemo.filter.idTo')}
              placeholder={t('integrations:c3AsrDemo.filter.idToPlaceholder')}
              rules={{ validate: validateIdRange }}
            />
            <Input
              control={control}
              name="limit"
              type="number"
              label={t('integrations:c3AsrDemo.filter.limit')}
              placeholder={t('integrations:c3AsrDemo.filter.limitPlaceholder')}
            />
          </div>
        </CardContent>
        <CardFooter className="flex flex-wrap gap-3">
          <Button type="submit" disabled={isStarting}>
            {t('integrations:c3AsrDemo.filter.load')}
          </Button>
        </CardFooter>
      </form>

      <CardContent>
        <AwaitContainer isLoading={isLoading || isFetching}>
          {items.length === 0 ? (
            <div className="text-muted-foreground p-2">{t('integrations:c3AsrDemo.table.empty')}</div>
          ) : (
            <Table
              data={items}
              columns={columns}
              getKey={(row) => row.sourceId}
              pagination={{ total, page, perPage: PER_PAGE }}
              type="paginated"
              onChangePagination={handleChangePagination}
              styles={{ size: 'sm' }}
            />
          )}
        </AwaitContainer>
      </CardContent>
    </Card>
  );
};
